---
title: "Searching and filtering your catalogue"
slug: "searching-and-filtering"
category: "books"
description: "Find any book fast — by title, author, availability, year group, or a mix of all four."
tags: ["search", "filter", "find", "catalogue"]
order: 2
---

Once your library has a few hundred books in it, you'll need to find specific ones quickly — a pupil asking "have you got anything by Michael Morpurgo?" shouldn't end with you scrolling through a list. BookBurrow's search is designed to give an answer in one or two keypresses.

## How search works

The big search box at the top of the Books tab searches across every relevant field at once:

- Title
- Author
- Publisher
- ISBN
- Item barcode
- Tags
- Reading scheme / level

As you type, the list below filters live. There's no **Search** button — results update on every keystroke.

Search is **case-insensitive** and **substring-based**: typing `morp` will match "Michael Morpurgo", "Morph", and "Metamorphosis". If you type more characters, you narrow the results further.

## Filters

To the right of the search box are filters. The most useful:

- **Availability**: All / Available / On loan. Handy when a pupil asks what they can take home today.
- **Year group**: filters to books tagged for that year group, if you use year-group tags.
- **Reading scheme**: filters to one particular scheme.
- **Category**: fiction, non-fiction, picture book, etc.
- **Recently added**: books you've catalogued in the last 30 days.

Filters combine with search: you can type `dino` with filter `Year 2` to find all dinosaur books suitable for Year 2.

## Example searches

Here are the kinds of things schools actually search for:

<Callout variant="tip" title="Practical examples">
- `morp` — every book by Michael Morpurgo
- `dino` + Year 2 filter — dinosaur books suitable for Year 2
- ISBN `9780140568`  — a specific book by partial ISBN
- `9781` with filter "Recently added" — all new books that still have a modern ISBN
- empty search + filter "On loan" — the complete list of books currently out
</Callout>

## Saving a search

If there's a filter combination you use every week — say, "available Year 6 fiction" — click **Save this view** next to the filters. It gets added to the sidebar and you can click it in one go next time.

## Searching by barcode on the Lend screen

On the [Lend and Return screens](/help/guides/lending-and-returns), the same search mechanism is used to accept scanned barcodes. Typing or scanning a number looks it up as an ISBN, an item barcode, or a card number, and picks the right match automatically. In practice, you never need to tell BookBurrow what type of number you're entering — it figures it out.

If you've just finished cataloguing a big box of new books via [ISBN lookup](/help/guides/scanning-books-isbn-lookup), the "Recently added" filter is the fastest way to make sure every one of them ended up where you expected.

## Sorting the results

Click any column header in the list to sort: title, author, date added, times borrowed, or status. Clicking twice reverses the sort order. The current sort is remembered per saved view, so "available Year 6 fiction sorted by most borrowed" gives you an instant popularity ranking for older pupils.

## When search doesn't find what you expect

Two things to check:

1. **Spelling**. Search is literal — `morpergo` won't match `morpurgo`. Try a shorter fragment.
2. **Filters**. A common mistake is to leave a filter set from a previous search. The filter chips above the list show what's currently active; click the **×** on each to clear them, or click **Clear all filters**.
